import React, { useEffect, useState } from "react";
import { api, fmtNumber } from "@/lib/apiClient";
import { TrendUp, TrendDown, Minus, Warning } from "@phosphor-icons/react";
import Sparkline from "@/components/Sparkline";

const STATUS_META = {
    on_track: { label: "On track", color: "#15803D", bg: "#DCFCE7", border: "#86EFAC", Icon: TrendUp },
    slight_underpace: { label: "Slight underpace", color: "#B45309", bg: "#FEF3C7", border: "#FCD34D", Icon: Warning },
    underpace: { label: "Underpace", color: "#B91C1C", bg: "#FEE2E2", border: "#FCA5A5", Icon: TrendDown },
    not_started: { label: "Not started", color: "#475569", bg: "#F1F5F9", border: "#CBD5E1", Icon: Minus },
    unknown: { label: "Unknown", color: "#71717A", bg: "#F4F4F5", border: "#E4E4E7", Icon: Minus },
};

export default function PaceWidget({ campaignId }) {
    const [pace, setPace] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/campaigns/${campaignId}/pace`);
                setPace(data);
            } catch (e) {
                setPace(null);
            } finally {
                setLoading(false);
            }
        })();
    }, [campaignId]);

    if (loading) return <div className="text-xs font-mono text-slate-400">loading pace…</div>;
    if (!pace) return null;

    const meta = STATUS_META[pace.status] || STATUS_META.unknown;
    const Icon = meta.Icon;
    const progressClamped = Math.min(pace.progress_percent, 100);
    const paceClamped = Math.min(pace.pace_percent, 200);

    return (
        <div className="bg-white border border-slate-200" data-testid="pace-widget">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
                <div>
                    <div className="overline">Forecast</div>
                    <h3 className="font-display text-lg">Delivery Pace</h3>
                </div>
                <span
                    className="font-mono text-[10px] uppercase tracking-wider px-2.5 py-1 inline-flex items-center gap-1.5"
                    style={{ color: meta.color, backgroundColor: meta.bg, border: `1px solid ${meta.border}` }}
                    data-testid="pace-status-badge"
                >
                    <Icon size={12} weight="bold" />
                    {meta.label}
                </span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-200 border-b border-slate-200">
                <PaceStat label="Target volume" value={fmtNumber(pace.target_volume)} />
                <PaceStat label="Delivered" value={fmtNumber(pace.delivered_impressions)} />
                <PaceStat label="Days elapsed" value={`${pace.days_elapsed} / ${pace.total_days}`} />
                <PaceStat label="Projected final" value={fmtNumber(pace.projected_final)} />
            </div>

            <div className="p-5 space-y-5">
                <Bar
                    label="Progress vs target"
                    value={`${progressClamped.toFixed(0)}%`}
                    percent={progressClamped}
                    barColor="#0033A0"
                    sub={`${fmtNumber(pace.delivered_impressions)} of ${fmtNumber(pace.target_volume)} impressions`}
                    testid="progress-bar"
                />
                <Bar
                    label="Pace vs expected to date"
                    value={`${paceClamped.toFixed(0)}%`}
                    percent={paceClamped > 100 ? 100 : paceClamped}
                    barColor={meta.color}
                    sub={pace.expected_to_date > 0
                        ? `Expected ${fmtNumber(pace.expected_to_date)} · Delivered ${fmtNumber(pace.delivered_impressions)}`
                        : "Awaiting first EOD report"}
                    testid="pace-bar"
                />

                {/* Sparkline + Week-over-week */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-slate-200 border border-slate-200" data-testid="wow-block">
                    <div className="bg-white p-4 md:col-span-2">
                        <div className="flex items-baseline justify-between mb-2">
                            <span className="overline">14-day delivery</span>
                            <span className="font-mono text-[10px] text-slate-500">impressions</span>
                        </div>
                        <Sparkline
                            data={(pace.daily_series || []).map((d) => d.impressions)}
                            width={520}
                            height={48}
                            color={meta.color}
                        />
                        <div className="flex justify-between mt-1.5">
                            <span className="overline text-slate-400" style={{ fontSize: 9 }}>
                                {pace.daily_series?.[0]?.date?.slice(5) || ""}
                            </span>
                            <span className="overline text-slate-400" style={{ fontSize: 9 }}>
                                {pace.daily_series?.[pace.daily_series.length - 1]?.date?.slice(5) || ""}
                            </span>
                        </div>
                    </div>
                    <WowCard
                        last7={pace.wow_last7_impressions || 0}
                        prev7={pace.wow_prev7_impressions || 0}
                        deltaPercent={pace.wow_percent || 0}
                    />
                </div>

                <div className="grid grid-cols-3 gap-px bg-slate-200 border border-slate-200">
                    <PaceStat label="Daily target" value={fmtNumber(Math.round(pace.daily_target))} small />
                    <PaceStat label="Days remaining" value={pace.days_remaining} small />
                    <PaceStat label="EOD reports" value={pace.report_count} small />
                </div>
            </div>
        </div>
    );
}

function PaceStat({ label, value, small }) {
    return (
        <div className="bg-white p-4">
            <div className="overline" style={{ fontSize: small ? 9 : 10 }}>{label}</div>
            <div className={`font-mono ${small ? "text-base" : "text-xl"} mt-0.5`}>{value}</div>
        </div>
    );
}

function WowCard({ last7, prev7, deltaPercent }) {
    const up = deltaPercent >= 0;
    const color = up ? "#15803D" : "#B91C1C";
    const Icon = up ? TrendUp : TrendDown;
    const noPrev = prev7 === 0 && last7 === 0;
    return (
        <div className="bg-white p-4 flex flex-col justify-between" data-testid="wow-card">
            <div>
                <div className="overline" style={{ fontSize: 10 }}>Week over week</div>
                <div className="flex items-baseline gap-2 mt-1">
                    {noPrev ? (
                        <span className="font-mono text-base text-slate-400">no data</span>
                    ) : (
                        <>
                            <span className="font-mono text-2xl" style={{ color }}>
                                {up ? "+" : ""}
                                {deltaPercent.toFixed(0)}%
                            </span>
                            <Icon size={16} weight="bold" style={{ color }} />
                        </>
                    )}
                </div>
            </div>
            <div className="text-[10px] font-mono text-slate-500 mt-2 leading-snug">
                <div>Last 7d · <span className="text-slate-900">{Number(last7).toLocaleString()}</span></div>
                <div>Prev 7d · <span className="text-slate-700">{Number(prev7).toLocaleString()}</span></div>
            </div>
        </div>
    );
}

function Bar({ label, value, percent, barColor, sub, testid }) {
    return (
        <div data-testid={testid}>
            <div className="flex items-baseline justify-between mb-1.5">
                <span className="overline">{label}</span>
                <span className="font-mono text-sm">{value}</span>
            </div>
            <div className="h-2 bg-slate-100 relative">
                <div
                    className="h-full transition-all duration-500"
                    style={{ width: `${percent}%`, backgroundColor: barColor }}
                />
            </div>
            {sub && <div className="text-[10px] font-mono text-slate-500 mt-1">{sub}</div>}
        </div>
    );
}
